/**
 * PortfoliYo Lab — the two display-time decisions the Visualizer makes about a tree.
 *
 * Split out of `DefinitionVisualizer` so they can be tested as what they are: pure functions over a
 * `LogicNode`, with no React, no network and no DOM. Both exist because of the same measurement —
 * the definitions this page is for are enormous, and both answer a question the server deliberately
 * stopped answering.
 *
 * `markHighlight` used to be a request parameter. Marking can only change what is PAINTED, so
 * sending it meant every keystroke in the highlight box re-uploaded the definition and re-parsed
 * it — 30 MB and 3.5 seconds to repaint some pills.
 *
 * `pickExpandedDepth` exists because a fixed collapse depth cannot serve both tree shapes, and
 * guessing one badly is the difference between a page that stalls and a page that shows nothing.
 */
import type { LogicNode } from '../components/LogicTree';

/**
 * How many nodes may be on screen before anyone expands anything.
 *
 * Spent as depth, not as a flat cap, by `pickExpandedDepth` below.
 */
export const RENDER_BUDGET_NODES = 800;

/**
 * The deepest level that fits inside `RENDER_BUDGET_NODES`, for `LogicTree.defaultExpandedDepth`.
 *
 * **A fixed depth cannot work here, and the measurement is why.** These trees come in two shapes.
 * A hand-written strategy is wide and shallow. A split cascade is the opposite: the 30 MB Borg
 * cascade draws 3,661 nodes and is FIFTY levels deep, so a depth of 6 shows seven nodes — a
 * responsive page displaying nothing — while depth 25 shows 785, which is most of the readable
 * tree. Choosing the depth from the tree's own shape gets both: small trees open whole (the walk
 * never reaches the budget) and deep ones open as far as they usefully can.
 */
export function pickExpandedDepth(node: LogicNode | null, budget: number): number {
  if (!node) return Infinity;

  const perDepth: number[] = [];
  const count = (current: LogicNode | null, depth: number): void => {
    if (!current) return;
    perDepth[depth] = (perDepth[depth] ?? 0) + 1;
    const n = current as Record<string, unknown>;
    for (const child of (n.children as LogicNode[] | undefined) ?? []) count(child, depth + 1);
    count((n.then as LogicNode | null) ?? null, depth + 1);
    count((n.else as LogicNode | null) ?? null, depth + 1);
  };
  count(node, 0);

  let running = 0;
  for (let depth = 0; depth < perDepth.length; depth += 1) {
    running += perDepth[depth] ?? 0;
    if (running > budget) return Math.max(depth - 1, 1);
  }
  return Infinity; // The whole tree fits; open all of it.
}

/**
 * Mark the branches that can allocate `symbol`, in place of the server that used to.
 *
 * Returns a NEW tree — React needs changed identity to re-render, and mutating the response would
 * make the marks stick when the highlight is cleared. `symbol_present` and `symbol_in_then` /
 * `symbol_in_else` are exactly the fields `logic_extractor` fills in for Allocation Analysis, so
 * `LogicTree` cannot tell which side computed them.
 */
export function markHighlight(node: LogicNode | null, symbol: string): LogicNode | null {
  if (!node) return null;
  const wanted = symbol.trim().toUpperCase();

  const walk = (current: LogicNode | null): { node: LogicNode | null; hit: boolean } => {
    if (!current) return { node: null, hit: false };
    const n = current as Record<string, unknown>;

    const assets = (n.assets as Array<{ ticker: string }> | undefined) ?? [];
    let hit = wanted !== '' && assets.some((a) => a.ticker.toUpperCase() === wanted);

    const children = (n.children as LogicNode[] | undefined) ?? [];
    const nextChildren = children.map((child) => {
      const result = walk(child);
      hit = hit || result.hit;
      return result.node as LogicNode;
    });

    const thenArm = walk((n.then as LogicNode | null) ?? null);
    const elseArm = walk((n.else as LogicNode | null) ?? null);
    hit = hit || thenArm.hit || elseArm.hit;

    return {
      // Through `unknown`: `n` is the node widened to a bag of keys so the walk can be written
      // once for five node types, and TypeScript will not narrow a bag back to the union on its
      // own. The shape is unchanged — every key of `current` is carried through.
      node: {
        ...n,
        ...(children.length > 0 ? { children: nextChildren } : {}),
        ...(n.then !== undefined ? { then: thenArm.node } : {}),
        ...(n.else !== undefined ? { else: elseArm.node } : {}),
        symbol_present: hit,
        symbol_in_then: thenArm.hit,
        symbol_in_else: elseArm.hit,
      } as unknown as LogicNode,
      hit,
    };
  };

  return walk(node).node;
}
